import { Button as ButtonPrimitive } from '@base-ui/react/button';
import { IconLoader2 } from '@tabler/icons-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/cn';
import { isValidElement, useEffect, useRef, useState } from 'react';

const isPromise = (value: unknown): value is PromiseLike<unknown> =>
  value != null && typeof (value as { then?: unknown }).then === 'function';

type ButtonClickEvent = Parameters<
  NonNullable<ButtonPrimitive.Props['onClick']>
>[0];

const buttonVariants = cva(
  // Disabled keys off data-disabled, not :disabled, so it still lands when
  // `render` swaps in a non-button element.
  "group/button inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent bg-clip-padding text-headline whitespace-nowrap transition-all focus-ring select-none active:not-aria-[haspopup]:translate-y-px data-disabled:pointer-events-none data-disabled:border-transparent data-disabled:bg-fill-quaternary data-disabled:bg-none data-disabled:text-foreground-tertiary data-disabled:shadow-none aria-invalid:border-intent-destructive aria-invalid:ring-3 aria-invalid:ring-intent-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        brand:
          'bg-accent-brand text-accent-brand-foreground hover:overlay-fill-quaternary',
        default:
          'bg-surface-inverse text-foreground-inverse hover:overlay-fill-secondary',
        secondary:
          'bg-fill-quaternary text-foreground hover:overlay-fill-quaternary aria-expanded:overlay-fill-quaternary aria-expanded:text-foreground',
        elevated:
          'border-line bg-surface-elevated text-foreground shadow-raised hover:overlay-fill-quaternary aria-expanded:overlay-fill-quaternary',
        ghost:
          'text-foreground-secondary hover:overlay-surface-secondary hover:text-foreground aria-expanded:bg-surface-secondary aria-expanded:text-foreground data-disabled:bg-transparent',
        destructive:
          'bg-intent-destructive-fill text-intent-destructive-fill-foreground hover:overlay-fill-quaternary',
      },
      size: {
        sm: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-subheadline-emphasized in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        default:
          'h-8 gap-1 px-3.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        lg: "h-10 gap-2 rounded-[10px] px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg:not([class*='size-'])]:size-5",
        icon: 'size-8',
        'icon-sm':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3.5",
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>['variant']
>;
export type ButtonSize = NonNullable<
  VariantProps<typeof buttonVariants>['size']
>;

interface ButtonProps
  extends
    Omit<ButtonPrimitive.Props, 'onClick'>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  onClick?: (event: ButtonClickEvent) => unknown;
}

export const Button = ({
  className,
  variant = 'default',
  size = 'default',
  loading = false,
  disabled,
  onClick,
  children,
  ...props
}: ButtonProps) => {
  const [isPending, setIsPending] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleClick = (event: ButtonClickEvent) => {
    const result = onClick?.(event);
    if (!isPromise(result)) return;
    setIsPending(true);
    const settle = () => {
      if (mountedRef.current) setIsPending(false);
    };
    // The handler owns error reporting; the loader clears on either outcome.
    void Promise.resolve(result).then(settle, settle);
  };

  const isLoading = loading || isPending;

  return (
    <ButtonPrimitive
      data-slot="button"
      nativeButton={
        isValidElement(props.render) ? props.render.type === 'button' : true
      }
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      onClick={handleClick}
      className={cn(
        buttonVariants({ variant, size }),
        isLoading &&
          "[&_[data-icon='inline-start']:not([data-slot='button-spinner'])]:hidden",
        className,
      )}
      {...props}
    >
      {isLoading && (
        <IconLoader2
          data-slot="button-spinner"
          data-icon="inline-start"
          className="motion-safe:animate-spin"
        />
      )}
      {children}
    </ButtonPrimitive>
  );
};
Button.displayName = 'Button';

export { buttonVariants };
